import React from "react"
import { useQuery } from "@tanstack/react-query"
import api from "../../services/api"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Skeleton } from "../ui/skeleton"
import { Separator } from "../ui/separator"
import { Progress } from "../ui/progress"
import { CheckCircle2, AlertTriangle, XCircle, Activity, RefreshCw } from "lucide-react"

// Wrapper to add client-side timeout
const fetchWithTimeout = async (fetchFn, timeoutMs = 5000) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  
  try {
    const result = await fetchFn()
    clearTimeout(timeoutId)
    return result
  } catch (error) {
    clearTimeout(timeoutId)
    if (error.name === 'AbortError') {
      throw new Error('Request timed out')
    }
    throw error
  }
}

function FoodSearchHealth() {
  const { data: health, isLoading, isFetching, dataUpdatedAt, isError, error } = useQuery({
    queryKey: ["food-search-health"],
    queryFn: () => fetchWithTimeout(() => api.getFoodSearchHealth(), 5000),
    refetchInterval: 30000, // Check every 30 seconds
    staleTime: 15000, // Consider data fresh for 15 seconds
    gcTime: 5 * 60 * 1000, // Keep cached data for 5 minutes
    retry: 1, // Only retry once
    retryDelay: 1000,
    // Keep showing old data while fetching new data
    placeholderData: (previousData) => previousData,
  })

  const lastChecked = dataUpdatedAt ? new Date(dataUpdatedAt) : null

  const getStatusIcon = (status) => {
    switch (status) {
      case "healthy":
        return <CheckCircle2 className="h-5 w-5 text-green-600" />
      case "degraded":
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />
      case "unhealthy":
      case "error":
        return <XCircle className="h-5 w-5 text-red-600" />
      default:
        return <Activity className="h-5 w-5 text-muted-foreground" />
    }
  }

  const getStatusBackground = (status) => {
    switch (status) {
      case "healthy":
        return "bg-green-100 dark:bg-green-900/30"
      case "degraded":
        return "bg-yellow-100 dark:bg-yellow-900/30"
      case "unhealthy":
      case "error":
        return "bg-red-100 dark:bg-red-900/30"
      default:
        return "bg-muted"
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case "healthy":
        return "All Systems Operational"
      case "degraded":
        return "Degraded Performance"
      case "unhealthy":
        return "Service Unavailable"
      default:
        return "Unknown Status"
    }
  }

  // Normalize response time to percentage (max 500ms = 100%)
  const getLatencyProgress = (ms) => {
    if (!ms) return 0
    return Math.min(100, (ms / 500) * 100)
  }

  // Show skeleton only on initial load (no cached data)
  if (isLoading && !health) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Food Search API
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Handle error state with no cached data
  if (isError && !health) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Food Search API
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full flex items-center justify-center bg-muted">
              <XCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="font-semibold text-muted-foreground">Unable to Check Status</p>
              <p className="text-sm text-muted-foreground">
                {error?.message || "Connection failed"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Food Search API
            {isFetching && (
              <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
            )}
          </CardTitle>
          <div
            className={`h-2.5 w-2.5 rounded-full ${
              health?.status === "healthy"
                ? "bg-green-500"
                : health?.status === "degraded"
                ? "bg-yellow-500"
                : "bg-red-500"
            }`}
          />
        </div>
      </CardHeader>
      <CardContent>
        {/* Status Header */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`h-10 w-10 rounded-full flex items-center justify-center ${getStatusBackground(
              health?.status
            )}`}
          >
            {getStatusIcon(health?.status)}
          </div>
          <div>
            <p className="font-semibold">{getStatusText(health?.status)}</p>
            <p className="text-sm text-muted-foreground">
              Last checked:{" "}
              {lastChecked ? lastChecked.toLocaleTimeString() : "Never"}
            </p>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Endpoint Status */}
        <div className="space-y-4">
          {/* Health Endpoint */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-2 w-2 rounded-full ${
                    health?.healthOk ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span>Health Endpoint</span>
              </div>
              {health?.healthResponseTime && (
                <span className="text-muted-foreground tabular-nums">
                  {health.healthResponseTime}ms
                </span>
              )}
            </div>
            {health?.healthResponseTime && (
              <div className="relative">
                <Progress
                  value={getLatencyProgress(health.healthResponseTime)}
                  className="h-1.5"
                />
              </div>
            )}
          </div>

          {/* Search Test */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-2 w-2 rounded-full ${
                    health?.searchOk ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span>Search Test</span>
                {health?.searchResultCount !== null &&
                  health?.searchResultCount !== undefined && (
                    <span className="text-muted-foreground">
                      ({health.searchResultCount} results)
                    </span>
                  )}
              </div>
              {health?.searchResponseTime && (
                <span className="text-muted-foreground tabular-nums">
                  {health.searchResponseTime}ms
                </span>
              )}
            </div>
            {health?.searchResponseTime && (
              <div className="relative">
                <Progress
                  value={getLatencyProgress(health.searchResponseTime)}
                  className="h-1.5"
                />
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {health?.error && (
          <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-start gap-2">
            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{health.error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default FoodSearchHealth
